// import {
//   LayoutDashboard,
//   Users,
//   Building2,
//   UserCheck,
//   Gift,
//   ShoppingCart,
//   CreditCard,
//   Settings,
//   UserPlus
// } from 'lucide-react';

import DashboardIcon from "../assets/sidebar/DashbaordIcon";
import SettingsIcon from "../assets/sidebar/SettingsIcon";
import PaymentIcon from "../assets/sidebar/PaymentIcon";
import UserManageIcon from "../assets/sidebar/UserManageIcon";
import OrderIcon from "../assets/sidebar/OrderIcon";
import SubscriptionIcon from "../assets/sidebar/SubscriptionIcon";

import { STAFF_PERMISSIONS } from "../constants/permissions";

/**
 * ROUTE CONFIGURATION GUIDE
 *
 * Basic Route Structure:
 * - path: URL path (use :param for dynamic segments, /* for wildcards)
 * - component: Component name (must match key in routerGenerator.js componentMap)
 * - title: Display name for breadcrumbs and default navbar
 * - icon: Lucide icon component (for sidebar only)
 * - navbarTitle: Custom navbar title (optional, defaults to title)
 * - sidebar: true = show in sidebar menu
 * - navbar: true = show in navbar (currently unused, for future)
 * - exact: true = exact path match, false = prefix match
 * - parentPath: Path to parent route (for breadcrumbs)
 * - badge: Badge text/number (for sidebar item)
 *
 * Submenu Structure:
 * - hasSubMenu: true = expandable menu with children
 * - subMenu: Array of child routes (same structure as routes)
 *
 * Tab Structure (for single-page multi-tab routes):
 * - tabs: Array of { id, path, label } for internal navigation
 *
 * Examples:
 * 1. Simple route: { path: '/admin/dashboard', component: 'Dashboard', sidebar: true }
 * 2. Dynamic route: { path: '/admin/users/:id', component: 'UserProfile', sidebar: false }
 * 3. Wildcard route: { path: '/admin/orders/*', component: 'Orders', exact: false }
 * 4. With parent: { path: '/admin/users/:id/edit', parentPath: '/admin/users' }
 */

export const ADMIN_ROUTES = {
  DASHBOARD: {
    path: "/admin/dashboard", // URL path
    component: "Dashboard", // Must exist in componentMap
    title: "Dashboard", // Sidebar & default navbar title
    icon: DashboardIcon, // Sidebar icon (Lucide component)
    navbarTitle: "Dashboard", // Custom navbar title (optional)
    sidebar: true, // Show in sidebar
    navbar: true, // Reserved for future use
    exact: true, // Exact path match
    requiredPermissions: [], // Everyone can see dashboard
  },

  ADMIN_PROFILE: {
    path: "/admin/profile",
    component: "AdminProfile",
    title: "My Profile",
    navbarTitle: "My Profile",
    sidebar: false,
    navbar: true,
    exact: true,
  },

  ADD_TEAM_MEMBER: {
    path: "/admin/add-team-member",
    component: "AddTeamMember",
    title: "Add Team Member",
    navbarTitle: "Team Member",
    sidebar: false,
    navbar: false,
    exact: true,
  },

  CHANGE_PASSWORD: {
    path: "/admin/change-password",
    component: "ChangePassword",
    title: "Change Password",
    navbarTitle: "Change Password",
    sidebar: false,
    navbar: true,
    exact: true,
  },

  USER_MANAGEMENT: {
    path: "/admin/users", // Parent path
    title: "User Manage", // Parent menu title
    icon: UserManageIcon, // Parent menu icon
    sidebar: true, // Show parent in sidebar
    hasSubMenu: true, // Enable submenu
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_USERS], // Need canViewUsers
    subMenu: [
      // Child routes
      {
        path: "/admin/users/all",
        component: "AllUsers",
        title: "All Users",
        sidebar: true, // Show in submenu
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_USERS],
      },
      {
        path: "/admin/users/company",
        component: "CompanyUsers",
        title: "Company Users",
        navbarTitle: "Company Users",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_COMPANIES],
      },
      {
        path: "/admin/users/referral",
        component: "ReferralUsers",
        title: "Referral Users",
        navbarTitle: "Referral Users",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_USERS],
      },
      {
        path: "/admin/users/verify",
        component: "VerifyUsers",
        title: "Verify Users",
        navbarTitle: "Verify Users",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_MANAGE_USERS],
      },
    ],
  },

  // Detail page (not in sidebar, has parent)
  USER_PROFILE: {
    path: "/admin/users/:userId/profile", // Dynamic segment :userId
    component: "UserProfile",
    title: "User Profile",
    navbarTitle: "User Profile", // Or 'All Users / User Profile'
    sidebar: false, // Hide from sidebar
    navbar: false,
    parentPath: "/admin/users/all", // For breadcrumb reference
    exact: false,
  },

  COMPANY_PROFILE: {
    path: "/admin/companies/:companyId/profile",
    component: "CompanyProfile",
    title: "Company Profile",
    navbarTitle: "Company Profile",
    sidebar: false,
    navbar: false,
    parentPath: "/admin/users/company",
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_COMPANIES],
  },

  // Wildcard route with tabs (single component, multiple URLs)
  ORDERS: {
    path: "/admin/orders/*", // Matches all /admin/orders/...
    component: "Orders", // Single component handles all tabs
    title: "Orders",
    icon: OrderIcon,
    navbarTitle: "Orders",
    sidebar: true,
    badge: "5", // Badge on sidebar item
    exact: false, // Allow child paths
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_ORDERS], // Need canViewOrders
    tabs: [
      // Define tab routes (for internal use)
      { id: "all", path: "/admin/orders/all", label: "All" },
      { id: "completed", path: "/admin/orders/completed", label: "Completed" },
      { id: "revision", path: "/admin/orders/revision", label: "Revision" },
      { id: "progress", path: "/admin/orders/progress", label: "In Progress" },
      {
        id: "subscription",
        path: "/admin/orders/subscription",
        label: "Subscription",
      },
    ],
  },

  ORDER_DETAILS: {
    path: "/admin/orders/:orderId/order",
    component: "OrderDetails",
    title: "Order Details",
    navbarTitle: "Order Details", // Show only "Order Details"
    // OR
    // titleDisplay: 'breadcrumb', // Show "Orders / Order Details"
    // OR
    // titleDisplay: 'parent', // Show only "Orders"
    sidebar: false,
    navbar: false,
    parentPath: "/admin/orders/all",
  },

  ORDER_CHAT: {
    path: "/admin/orders/:orderId/chat",
    component: "OrderChat",
    title: "Order Chat",
    navbarTitle: "Order Chat",
    sidebar: false,
    navbar: false,
    parentPath: "/admin/orders/all",
  },

  CREATE_OFFER: {
    path: "/admin/create-offer/:offerId?",
    component: "CreateOffer",
    title: "Create Offer",
    navbarTitle: "Create Offer",
    sidebar: false,
    navbar: true,
    exact: false,
  },

  VIEW_OFFERS: {
    path: "/admin/view-offers",
    component: "ViewOffers",
    title: "View Offers",
    navbarTitle: "View Offers",
    sidebar: false,
    navbar: true,
    exact: true,
  },

  // ADD_TEAM_MEMBER: {
  //   path: '/admin/add-team-member',
  //   component: 'AddTeamMember',
  //   title: 'Add Team Member',
  //   navbarTitle: 'Add Team Member',
  //   icon: UserPlus,
  //   sidebar: true,
  //   navbar: true,
  //   exact: true
  // },

  PAYMENTS: {
    path: "/admin/payments",
    component: "Payments",
    title: "Payments",
    navbarTitle: "Payments",
    icon: PaymentIcon,
    sidebar: true,
    navbar: true,
    exact: true,
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_FINANCIALS], // Need financial permission
  },

  PAYMENT_PAGE: {
    path: "/admin/payment-page/:offerId",
    component: "Payment",
    title: "Payment",
    navbarTitle: "Payment",
    sidebar: false,
    navbar: false,
    exact: true,
  },

  CLIENT_DETAILS: {
    path: "/admin/clients/:clientId",
    component: "ClientDetails",
    title: "Client Profile",
    navbarTitle: "Client Profile",
    sidebar: false,
    navbar: false,
    exact: true,
  },

  FULL_CHAT_VIEW: {
    path: "/admin/full-chat-view",
    component: "FullChatView",
    title: "Chat",
    navbarTitle: "Chat",
    sidebar: false,
    hideSidebar: true,
    exact: true,
  },

  SETTINGS: {
    path: "/admin/settings",
    title: "Settings",
    navbarTitle: "Settings",
    icon: SettingsIcon,
    sidebar: true,
    hasSubMenu: true,
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_SETTINGS], // Need settings permission
    subMenu: [
      {
        path: "/admin/settings/platform",
        component: "PlatformSettings",
        title: "Platform Settings",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_MANAGE_SETTINGS],
      },
      {
        path: "/admin/settings/notifications",
        component: "NotificationSettings",
        title: "Notifications",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_MANAGE_SETTINGS],
      },
      {
        path: "/admin/settings/theme",
        component: "ThemeSettings",
        title: "Change Theme",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [],
      },
    ],
  },

  SUBSCRIPTION: {
    path: "/admin/subscriptions",
    title: "Subscriptions",
    navbarTitle: "Subscriptions",
    icon: SubscriptionIcon,
    sidebar: true,
    hasSubMenu: true,
    requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_SUBSCRIPTIONS], // Need subscription permission
    subMenu: [
      {
        path: "/admin/subscriptions/orders",
        component: "SubscriptionOrders",
        title: "Projects",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_VIEW_SUBSCRIPTIONS], // Need subscription permission
      },
      {
        path: "/admin/subscriptions/:subscriptionId",
        component: "SubscriptionManagement",
        title: "Management",
        sidebar: false,
        navbar: false,
        exact: true,
      },
      {
        path: "/admin/subscriptions/plan-management/*",
        component: "SubscriptionPlanManagement",
        title: "Plan",
        sidebar: true,
        navbar: true,
        exact: true,
        requiredPermissions: [STAFF_PERMISSIONS.CAN_MANAGE_SUBSCRIPTIONS], // Need manage permission
        tabs: [
          // Define tab routes (for internal use)
          {
            id: "maintenanc-plans",
            path: "/admin/subscriptions/plan-management/maintenance-plans",
            label: "Maintenanc Plans",
          },
          {
            id: "seo-plans",
            path: "/admin/subscriptions/plan-management/seo-plans",
            label: "SEO Plans",
          },
          {
            id: "security-plans",
            path: "/admin/subscriptions/plan-management/security-plans",
            label: "Security Plans",
          },
          {
            id: "bundle",
            path: "/admin/subscriptions/plan-management/bundle",
            label: "Bundle",
          },
        ],
      },
      {
        path: "/admin/subscriptions/ai-workflow",
        component: "AIworklowOrders",
        navbarTitle: "AI Workflow Projects",
        title: "AI Workflow",
        sidebar: true,
        navbar: true,
        exact: true,
      },
      {
        path: "/admin/subscriptions/ai-workflow/:subscriptionId",
        component: "UserAiWorkFlowManagement",
        navbarTitle: "AI Workflow Management",
        title: "AI Workflow Management",
        sidebar: false,
        navbar: false,
        exact: true,
      }
    ],
  },
};

export const PUBLIC_ROUTES = {
  SIGNIN: {
    path: "/signin",
    component: "SignIn",
    title: "Sign In",
  },
  FORGOT_PASSWORD: {
    path: "/forgot-password",
    component: "ForgotPassword",
    title: "Forgot Password",
  },
  RESET_PASSWORD: {
    path: "/reset-password", // For token-based resets
    component: "SetNewPassword",
    title: "Reset Password",
  },
  FIRST_LOGIN_RESET: {
    path: "/first-login-reset", // For first login resets
    component: "FirstLoginResetPasswordPage",
    title: "Set Password",
  },

  // TEST PAGES
  CONFIRMATION_TEST: {
    path: "/test-confirmation",
    component: "UseConfirmationTesting",
    title: "Test Confirmation",
  },
  REUSABLE: {
    path: "/reusable",
    component: "TestReusableComponents",
    title: "Reusable Components",
  },
};

/**
 * REFERENCE
 *
 * Route Visibility:
 * - sidebar: true, navbar: true     → Shows in sidebar and navbar
 * - sidebar: false, navbar: false   → Hidden (detail/edit pages)
 *
 * Path Types:
 * - /admin/dashboard               → Static path
 * - /admin/users/:id               → Dynamic parameter
 * - /admin/orders/*                → Wildcard (catches all children)
 *
 * Title Display:
 * - navbarTitle: 'Title'           → Shows "Title"
 * - navbarTitle: 'Parent / Title'  → Shows breadcrumb
 * - No navbarTitle                 → Uses title attribute
 *
 * Special Attributes:
 * - parentPath: '/admin/users'     → Links to parent (for breadcrumbs)
 * - hasSubMenu: true               → Enables expandable menu
 * - tabs: [...]                    → Internal tab navigation
 * - badge: '5'                     → Shows badge on sidebar item
 * - exact: true                    → Exact URL match only
 */
